import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { MessagingMessageCreatedPayload } from '@/shared/realtime/echo'
import { useAuthStore } from '@/modules/auth'

export type MessagingNotificationItem = {
  id: string
  phoneNumberId: string
  accountId: string | null
  threadId: string
  title: string
  preview: string
  occurredAt: string
  isNew: boolean
}

const SOUND_KEY_PREFIX = 'pv_messaging_notification_sound'
const MAX_ITEMS = 50

function soundStorageKey(userId: string | number | null | undefined): string {
  return `${SOUND_KEY_PREFIX}:${userId ?? 'anon'}`
}

function readSoundEnabled(userId: string | number | null | undefined): boolean {
  try {
    const raw = localStorage.getItem(soundStorageKey(userId))
    if (raw === null) return true
    return raw === '1' || raw === 'true'
  } catch {
    return true
  }
}

function writeSoundEnabled(userId: string | number | null | undefined, enabled: boolean): void {
  try {
    localStorage.setItem(soundStorageKey(userId), enabled ? '1' : '0')
  } catch {
    // ignore quota / private mode
  }
}

function previewFromMessage(message: Record<string, unknown> | null | undefined): string {
  if (!message) return 'New message'
  const text = typeof message.text === 'string' ? message.text.trim() : ''
  if (text) return text.length > 120 ? `${text.slice(0, 117)}…` : text
  if (message.is_media) return 'Media message'
  return 'New message'
}

function titleFromThread(thread: Record<string, unknown> | null | undefined): string {
  if (!thread) return 'SMS'
  const label = typeof thread.phonebook_label === 'string' ? thread.phonebook_label.trim() : ''
  if (label) return label
  const formatted = thread.formatted_recipient_phone_numbers
  if (Array.isArray(formatted) && formatted.length > 0) {
    return formatted.filter((v): v is string => typeof v === 'string').join(', ') || 'SMS'
  }
  const recipients = thread.recipient_phone_numbers
  if (Array.isArray(recipients) && recipients.length > 0) {
    return recipients.filter((v): v is string => typeof v === 'string').join(', ') || 'SMS'
  }
  return 'SMS'
}

export const useMessagingNotificationsStore = defineStore('messagingNotifications', () => {
  const items = ref<MessagingNotificationItem[]>([])
  const soundEnabled = ref(true)
  const hydrated = ref(false)
  const loading = ref(false)

  const unreadCount = computed(() => items.value.length)

  function syncSoundFromUser(userId: string | number | null | undefined) {
    soundEnabled.value = readSoundEnabled(userId)
  }

  function setSoundEnabled(enabled: boolean) {
    soundEnabled.value = enabled
    const auth = useAuthStore()
    writeSoundEnabled(auth.user?.id, enabled)
  }

  function toggleSound() {
    setSoundEnabled(!soundEnabled.value)
  }

  function clearItems() {
    items.value = []
    hydrated.value = false
  }

  function resetForSessionChange() {
    clearItems()
    const auth = useAuthStore()
    syncSoundFromUser(auth.user?.id)
  }

  function upsertItem(item: MessagingNotificationItem) {
    const without = items.value.filter((existing) => existing.threadId !== item.threadId)
    items.value = [item, ...without].slice(0, MAX_ITEMS)
  }

  function addFromRealtimePayload(
    payload: MessagingMessageCreatedPayload,
    accountId: string | null = null
  ) {
    if (payload.direction !== 'inbound') return

    const thread = (payload.thread || {}) as Record<string, unknown>
    const message = (payload.message || {}) as Record<string, unknown>
    const threadId = String(payload.message_thread_id || thread.id || '')
    const phoneNumberId = String(payload.phone_number_id || thread.phone_number_id || '')
    if (!threadId || !phoneNumberId) return

    const occurredAt =
      (typeof message.created_at === 'string' && message.created_at) ||
      (typeof thread.latest_at === 'string' && thread.latest_at) ||
      new Date().toISOString()

    upsertItem({
      id: `${threadId}:${typeof message.id === 'string' ? message.id : occurredAt}`,
      phoneNumberId,
      accountId:
        accountId || (typeof thread.account_id === 'string' ? thread.account_id : null) || null,
      threadId,
      title: titleFromThread(thread),
      preview: previewFromMessage(message),
      occurredAt,
      isNew: true
    })
  }

  function addFromUnreadThread(
    thread: {
      id: string
      phone_number_id: string
      phonebook_label?: string | null
      formatted_recipient_phone_numbers?: string[]
      recipient_phone_numbers?: string[]
      latest_at?: string | null
      latest_message?: Record<string, unknown> | null
      is_read?: boolean
    },
    accountId: string | null = null
  ) {
    if (thread.is_read) return
    const latest = (thread.latest_message || null) as Record<string, unknown> | null
    upsertItem({
      id: `unread:${thread.id}`,
      phoneNumberId: String(thread.phone_number_id),
      accountId,
      threadId: String(thread.id),
      title: titleFromThread(thread as unknown as Record<string, unknown>),
      preview: previewFromMessage(latest),
      occurredAt: thread.latest_at || new Date().toISOString(),
      isNew: false
    })
  }

  /** Drop a thread from the bell after the user opens it (page marks read). */
  function dismissThread(threadId: string) {
    items.value = items.value.filter((item) => item.threadId !== threadId)
  }

  function markHydrated() {
    hydrated.value = true
  }

  return {
    items,
    soundEnabled,
    hydrated,
    loading,
    unreadCount,
    syncSoundFromUser,
    setSoundEnabled,
    toggleSound,
    clearItems,
    resetForSessionChange,
    addFromRealtimePayload,
    addFromUnreadThread,
    dismissThread,
    markHydrated,
    MAX_ITEMS
  }
})
